import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, CheckCircle2, ScanFace, Loader2 } from "lucide-react";
import { loadFaceModels, detectEnrollFace } from "@/lib/faceapi";

export default function FaceEnroll() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loadingModels, setLoadingModels] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [captured, setCaptured] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    api.get(`/face-invite/${token}`).then((r) => setInfo(r.data)).catch(() => setError("Convite inválido ou expirado"));
    loadFaceModels().then(() => setLoadingModels(false)).catch(() => setLoadingModels(false));
  }, [token]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 480, height: 640 } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setStreaming(true);
    } catch {
      toast.error("Câmera não disponível");
    }
  };
  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; setStreaming(false); };

  const capture = async () => {
    if (!videoRef.current) return;
    try {
      const result = await detectEnrollFace(videoRef.current);
      if (result.problem) return toast.error(result.problem);
      const c = canvasRef.current;
      c.width = videoRef.current.videoWidth;
      c.height = videoRef.current.videoHeight;
      const ctx = c.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0);
      const dataUrl = c.toDataURL("image/jpeg", 0.7);
      setCaptured({ photo: dataUrl, descriptor: result.descriptor });
      stop();
      toast.success("Rosto detectado!");
    } catch (e) {
      toast.error("Erro na detecção");
    }
  };

  const save = async () => {
    if (!captured) return;
    setSaving(true);
    try {
      await api.post(`/face-invite/${token}/enroll`, {
        face_photo: captured.photo,
        face_embedding: captured.descriptor,
      });
      setDone(true);
      toast.success("Face cadastrada!");
    } catch (e) {
      toast.error("Erro ao salvar");
    } finally { setSaving(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
      <Card className="p-6 bg-slate-900 border-slate-800 text-white text-center max-w-sm">
        <p className="text-lg font-heading font-bold">Convite inválido</p>
        <p className="text-slate-400 text-sm mt-2">Peça um novo link na recepção da academia.</p>
      </Card>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
      <Card className="p-8 bg-slate-900 border-slate-800 text-white text-center max-w-sm">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4"/>
        <h1 className="font-heading text-2xl font-bold">Tudo pronto!</h1>
        <p className="text-slate-400 text-sm mt-2">Seu rosto foi cadastrado. Agora é só passar na catraca da academia.</p>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 flex flex-col">
      <div className="max-w-md mx-auto w-full pt-4">
        <div className="flex items-center gap-2 mb-6">
          <ScanFace className="w-6 h-6 text-rose-500"/>
          <h1 className="font-heading text-2xl font-bold">Cadastro Facial</h1>
        </div>
        {info && (
          <Card className="p-4 bg-slate-900 border-slate-800 text-white mb-4">
            <div className="text-xs uppercase tracking-widest text-rose-400">Olá</div>
            <div className="font-heading text-xl font-bold">{info.student.name}</div>
            <div className="text-xs text-slate-400 mt-1">Vamos cadastrar seu rosto para entrar na catraca sem cartão.</div>
          </Card>
        )}

        <div className="aspect-[3/4] bg-black rounded-2xl overflow-hidden relative mb-4">
          {captured ? (
            <img src={captured.photo} alt="capture" className="w-full h-full object-cover" data-testid="face-enroll-preview"/>
          ) : (
            <>
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }}/>
              {streaming && <div className="absolute inset-8 border-4 rounded-full face-frame pointer-events-none"/>}
              {!streaming && <div className="absolute inset-0 flex items-center justify-center text-slate-500"><Camera className="w-16 h-16"/></div>}
            </>
          )}
        </div>
        <canvas ref={canvasRef} className="hidden"/>

        {loadingModels ? (
          <Button disabled className="w-full bg-slate-800"><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Carregando modelo facial...</Button>
        ) : !streaming && !captured ? (
          <Button onClick={start} data-testid="face-enroll-start-button" className="w-full bg-rose-600 hover:bg-rose-700">
            <Camera className="w-4 h-4 mr-2"/>Abrir câmera
          </Button>
        ) : streaming ? (
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={capture} data-testid="face-enroll-capture-button" className="bg-rose-600 hover:bg-rose-700">Capturar rosto</Button>
            <Button onClick={stop} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-900">Cancelar</Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={save} disabled={saving} data-testid="face-enroll-save-button" className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Salvando</> : "Confirmar rosto"}
            </Button>
            <Button onClick={() => { setCaptured(null); start(); }} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-900">Refazer</Button>
          </div>
        )}
        <p className="text-xs text-slate-500 mt-4 text-center">Enquadre seu rosto no círculo e mantenha boa iluminação.</p>
      </div>
    </div>
  );
}
